/* Environment Map — Detail Panels (Node, Edge, Change History) */
import { motion } from 'framer-motion';
import { X, Star, Warning, Lightning, Clock, CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { HEALTH_COLORS, EDGE_COLORS } from './constants';

export function NodeDetailPanel({ node, edges, onClose, onImpact }) {
  if (!node) return null;
  var connEdges = edges.filter(function (e) { return e.source_node_id === node.node_id || e.target_node_id === node.node_id; });
  return (
    <motion.div initial={{ x: 320, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 320, opacity: 0 }} className="absolute top-0 right-0 w-[320px] h-full bg-kova-card border-l border-kova-cyan/12 overflow-y-auto z-20" data-testid="node-detail-panel">
      <div className="sticky top-0 bg-kova-card border-b border-kova-cyan/12 px-4 py-3 flex items-center justify-between z-10">
        <div className="flex items-center gap-2">
          {node.is_critical_path && <Star size={12} weight="fill" className="text-kova-gold" />}
          <span className="text-sm font-heading font-bold truncate">{node.label}</span>
        </div>
        <button onClick={onClose} className="text-kova-slate hover:text-white"><X size={16} /></button>
      </div>
      <div className="p-4 space-y-4">
        <div className="space-y-2 text-xs">
          <div className="flex justify-between"><span className="text-kova-slate">Provider</span><span className="font-mono">{node.provider || '\u2014'}</span></div>
          <div className="flex justify-between"><span className="text-kova-slate">Type</span><span className="font-mono">{node.node_type}</span></div>
          <div className="flex justify-between"><span className="text-kova-slate">Category</span><span className="font-mono">{node.category}</span></div>
          <div className="flex justify-between"><span className="text-kova-slate">Health</span><span className="font-mono flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: HEALTH_COLORS[node.health_status] }} />{node.health_status}</span></div>
          {node.is_critical_path && <div className="flex items-center gap-1 px-2 py-1 bg-kova-gold/10 border border-kova-gold/20 text-[10px] font-mono text-kova-gold"><Star size={10} weight="fill" />Critical path node</div>}
          {node.has_breaking_drift && <div className="flex items-center gap-1 px-2 py-1 bg-kova-gold/10 border border-kova-gold/20 text-[10px] font-mono text-kova-gold"><Warning size={12} />Schema drift</div>}
        </div>
        <div>
          <div className="text-[10px] font-mono text-kova-slate uppercase tracking-wider mb-2">Connected edges ({connEdges.length})</div>
          {connEdges.length === 0 ? <p className="text-xs text-kova-slate">No connections</p> : (
            <div className="space-y-1">
              {connEdges.map(function (e) {
                var dir = e.source_node_id === node.node_id ? '\u2192' : '\u2190';
                var other = e.source_node_id === node.node_id ? e.target_node_id : e.source_node_id;
                return (
                  <div key={e.edge_id} className="flex items-center gap-2 px-2 py-1 bg-kova-bg border border-kova-cyan/12 text-[10px] font-mono">
                    <span style={{ color: EDGE_COLORS[e.edge_type] }}>{e.edge_type}</span>
                    <span className="text-kova-slate">{dir}</span>
                    <span className="text-kova-silv truncate">{other.length > 15 ? other.slice(0, 14) + '\u2026' : other}</span>
                    {e.error_rate_7d > 0.05 && <span className="text-kova-red ml-auto">{Math.round(e.error_rate_7d * 100)}% err</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <Button onClick={function () { onImpact(node.node_id); }} size="sm" variant="outline" className="w-full rounded-none border-kova-red/30 text-kova-red hover:bg-kova-red/10 font-mono text-[10px] uppercase tracking-wider" data-testid="impact-analysis-btn">
          <Lightning size={12} weight="fill" className="mr-1" />Impact Analysis
        </Button>
      </div>
    </motion.div>
  );
}

export function EdgeDetailPanel({ edge, onClose }) {
  if (!edge) return null;
  var mappings = edge.field_mappings || [];
  return (
    <motion.div initial={{ x: 320, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 320, opacity: 0 }} className="absolute top-0 right-0 w-[320px] h-full bg-kova-card border-l border-kova-cyan/12 overflow-y-auto z-20" data-testid="edge-detail-panel">
      <div className="sticky top-0 bg-kova-card border-b border-kova-cyan/12 px-4 py-3 flex items-center justify-between z-10">
        <span className="text-sm font-heading font-bold" style={{ color: EDGE_COLORS[edge.edge_type] }}>{edge.edge_type} edge</span>
        <button onClick={onClose} className="text-kova-slate hover:text-white"><X size={16} /></button>
      </div>
      <div className="p-4 space-y-4">
        <div className="space-y-2 text-xs">
          <div className="flex justify-between"><span className="text-kova-slate">Source</span><span className="font-mono text-kova-silv">{edge.source_node || edge.source_node_id}</span></div>
          <div className="flex justify-between"><span className="text-kova-slate">Target</span><span className="font-mono text-kova-silv">{edge.target_node || edge.target_node_id}</span></div>
          <div className="flex justify-between"><span className="text-kova-slate">Frequency</span><span className="font-mono">{edge.frequency}</span></div>
          <div className="flex justify-between"><span className="text-kova-slate">Volume</span><span className="font-mono">{edge.volume_daily}/day</span></div>
          <div className="flex justify-between"><span className="text-kova-slate">Error rate</span><span className="font-mono" style={{ color: edge.error_rate_7d > 0.05 ? '#E24B4A' : '#1D9E75' }}>{Math.round((edge.error_rate_7d || 0) * 100)}%</span></div>
          <div className="flex justify-between"><span className="text-kova-slate">Criticality</span><span className={'font-mono ' + (edge.criticality === 'critical' ? 'text-kova-red' : edge.criticality === 'high' ? 'text-kova-gold' : 'text-kova-slate')}>{edge.criticality}</span></div>
        </div>
        {mappings.length > 0 && (
          <div>
            <div className="text-[10px] font-mono text-kova-purple uppercase tracking-wider mb-2">Field Mappings (F-003)</div>
            <div className="space-y-1">
              {mappings.map(function (m, i) {
                return (
                  <div key={i} className="flex items-center gap-2 px-2 py-1 bg-kova-purple/5 border border-kova-purple/15 text-[10px] font-mono" data-testid={'field-mapping-' + i}>
                    <span className="text-kova-silv">{m.source_field}</span>
                    <ArrowRight size={8} className="text-kova-purple" />
                    <span className="text-kova-silv">{m.target_field}</span>
                    <span className="text-kova-slate ml-auto">{m.transform_fn}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export function ChangeHistoryPanel({ events, onClose, onAck }) {
  return (
    <motion.div initial={{ x: 320, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 320, opacity: 0 }} className="absolute top-0 right-0 w-[320px] h-full bg-kova-card border-l border-kova-cyan/12 overflow-y-auto z-20" data-testid="change-history-panel">
      <div className="sticky top-0 bg-kova-card border-b border-kova-cyan/12 px-4 py-3 flex items-center justify-between z-10">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-kova-cyan" />
          <span className="text-sm font-heading font-bold">Change History</span>
        </div>
        <button onClick={onClose} className="text-kova-slate hover:text-white"><X size={16} /></button>
      </div>
      <div className="divide-y divide-kova-cyan/6">
        {events.length === 0 ? (
          <div className="p-4 text-center text-xs font-mono text-kova-slate">No changes recorded</div>
        ) : events.map(function (ev) {
          var sevColor = ev.severity === 'critical' ? '#E24B4A' : ev.severity === 'warning' ? '#C8A030' : '#3D5A70';
          return (
            <div key={ev.event_id} className="px-4 py-3 space-y-1" data-testid={'change-event-' + ev.event_id}>
              <div className="flex items-center gap-2">
                <span className="text-[8px] font-mono font-bold px-1.5 py-0.5 uppercase tracking-wider" style={{ color: sevColor, background: sevColor + '15', border: '1px solid ' + sevColor + '30' }}>{ev.event_type}</span>
                <span className="text-[9px] font-mono text-kova-slate ml-auto">{new Date(ev.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}</span>
              </div>
              <p className="text-[11px] text-kova-silv">{ev.description}</p>
              {!ev.acknowledged_by ? (
                <button onClick={function () { onAck(ev.event_id); }} className="text-[9px] font-mono text-kova-cyan hover:underline">Acknowledge</button>
              ) : (
                <span className="text-[9px] font-mono text-kova-green flex items-center gap-1"><CheckCircle size={10} weight="fill" />Acknowledged</span>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
